/**
 * MunicipalityInteractionForm — append-only entry capture.
 *
 * Identity rules (Phase 1A):
 * - municipalityId: resolved from the job's `permitDetail.municipality`
 *   via `getMunicipalityId()`. If the job's municipality is not in the
 *   canonical roster, we DO NOT block — we log the interaction with
 *   `municipalityId: "unmapped"` and preserve the operator-visible
 *   string as `rawMunicipalityLabel`. UI surfaces a warning so coordinators
 *   know the municipality record is missing (and Phase 1B requirements
 *   will fall back to the generic profile).
 * - loggedBy: useCurrentRep().name → role label → "Unknown Operator".
 *   Never "system".
 * - followUpDate: native datetime-local input → ISO datetime. No
 *   relative phrases.
 *
 * On submit, calls `onSubmit(input)`; the parent owns the actual
 * `setJobs` call so we can keep this component pure.
 */
import { useMemo, useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { toast } from "sonner";
import {
  MUNICIPALITY_INTERACTION_TYPES,
  INTERACTION_TYPE_LABEL,
  municipalityInteractionInputSchema,
  type MunicipalityInteractionInput,
  type MunicipalityInteractionType,
  type ContactParty,
} from "@/data/permitting/municipalityLog";
import {
  getMunicipalityId,
  getMunicipalityById,
  type MunicipalityId,
} from "@/data/floridaMunicipalities";
import { useCurrentRep } from "@/hooks/useCurrentRep";
import { useRole } from "@/data/RoleContext";

const HOMEOWNER_CALL_SCRIPT =
  "Hi, this is [Your Name] from [Company]. We're working on the permit for your home improvement project. " +
  "I'm calling to confirm a few details and let you know the next steps. " +
  "Could you confirm your email address and let me know your availability for a document signature?";

interface Props {
  /** Display string from `job.permitDetail.municipality`. */
  municipality: string;
  /** Optional override entry id (this submission supersedes that entry). */
  supersedesId?: string;
  /** Whether to default to homeowner or municipality mode. */
  defaultContactType?: ContactParty;
  onSubmit: (input: MunicipalityInteractionInput) => void;
  onCancel?: () => void;
}

function resolveLoggedBy(repName: string | undefined, roleLabel: string | undefined): string {
  if (repName && repName.trim()) return repName.trim();
  if (roleLabel && roleLabel.trim()) return roleLabel.trim();
  return "Unknown Operator";
}

export function MunicipalityInteractionForm({
  municipality,
  supersedesId,
  defaultContactType = "municipality",
  onSubmit,
  onCancel,
}: Props) {
  const { repName } = useCurrentRep();
  const { roleInfo } = useRole();
  const loggedBy = useMemo(
    () => resolveLoggedBy(repName, roleInfo?.label),
    [repName, roleInfo?.label],
  );

  const [contactType, setContactType] = useState<ContactParty>(defaultContactType);
  const isHomeowner = contactType === "homeowner";

  const resolvedId: MunicipalityId | null = useMemo(
    () => getMunicipalityId(municipality),
    [municipality],
  );
  const isUnmapped = !resolvedId;
  const municipalityId: MunicipalityId = resolvedId ?? "unmapped";
  const municipalityName = resolvedId
    ? getMunicipalityById(resolvedId)?.name ?? municipality
    : municipality || "Unmapped municipality";

  const [type, setType] = useState<MunicipalityInteractionType>("call");
  const [contactPerson, setContactPerson] = useState("");
  const [summary, setSummary] = useState("");
  const [nextStep, setNextStep] = useState("");
  const [followUpLocal, setFollowUpLocal] = useState(""); // datetime-local string

  const submit = () => {
    let followUpDate: string | null = null;
    if (followUpLocal) {
      const d = new Date(followUpLocal);
      if (Number.isNaN(d.getTime())) {
        toast.error("Invalid follow-up date");
        return;
      }
      followUpDate = d.toISOString();
    }
    const input: MunicipalityInteractionInput = {
      municipalityId,
      rawMunicipalityLabel: isUnmapped ? (municipality?.trim() || "Unknown") : undefined,
      type,
      contactPerson,
      summary,
      nextStep: nextStep.trim() ? nextStep.trim() : undefined,
      followUpDate,
      loggedBy,
      supersedesId,
      contactType,
    };
    const result = municipalityInteractionInputSchema.safeParse(input);
    if (!result.success) {
      toast.error(result.error.issues[0]?.message ?? "Invalid interaction");
      return;
    }
    onSubmit(result.data);
    // Reset
    setContactPerson("");
    setSummary("");
    setNextStep("");
    setFollowUpLocal("");
  };

  return (
    <div className="space-y-3 rounded-lg border p-4 bg-background">
      <div className="flex items-baseline justify-between gap-2">
        <h4 className="text-[11px] font-semibold text-muted-foreground uppercase tracking-wider">
          {supersedesId ? "Correct Earlier Interaction" : (isHomeowner ? "Log Homeowner Call" : "Log Municipality Interaction")}
        </h4>
        <span className="text-[11px] text-muted-foreground truncate">
          {municipalityName}
        </span>
      </div>

      {/* Contact type toggle */}
      {!supersedesId && (
        <div className="flex items-center gap-1 rounded-md border p-1 w-fit">
          <button
            type="button"
            onClick={() => setContactType("municipality")}
            className={`text-[11px] px-2.5 py-1 rounded transition-colors ${
              !isHomeowner
                ? "bg-primary text-primary-foreground font-medium"
                : "text-muted-foreground hover:text-foreground"
            }`}
          >
            Municipality
          </button>
          <button
            type="button"
            onClick={() => setContactType("homeowner")}
            className={`text-[11px] px-2.5 py-1 rounded transition-colors ${
              isHomeowner
                ? "bg-primary text-primary-foreground font-medium"
                : "text-muted-foreground hover:text-foreground"
            }`}
          >
            Homeowner
          </button>
        </div>
      )}

      {isUnmapped && !isHomeowner && (
        <p className="text-xs text-warning bg-warning/10 border border-warning/30 rounded px-2 py-1.5">
          Municipality "{municipality || "—"}" isn't in the canonical roster.
          You can still log this interaction; it will be tagged as <span className="font-medium">unmapped</span>{" "}
          and requirement checks fall back to the generic profile.
        </p>
      )}

      {isHomeowner && (
        <details className="text-xs text-muted-foreground">
          <summary className="cursor-pointer hover:text-foreground">Call script</summary>
          <p className="mt-1 bg-muted/30 rounded p-2 border text-[11px] leading-relaxed">{HOMEOWNER_CALL_SCRIPT}</p>
        </details>
      )}

      <div className="grid grid-cols-2 gap-3">
        <div className="space-y-1.5">
          <Label className="text-xs">Type</Label>
          <Select value={type} onValueChange={(v) => setType(v as MunicipalityInteractionType)}>
            <SelectTrigger><SelectValue /></SelectTrigger>
            <SelectContent>
              {MUNICIPALITY_INTERACTION_TYPES.map((t) => (
                <SelectItem key={t} value={t}>{INTERACTION_TYPE_LABEL[t]}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <div className="space-y-1.5">
          <Label className="text-xs">{isHomeowner ? "Homeowner Name" : "Contact Person"}</Label>
          <Input
            value={contactPerson}
            onChange={(e) => setContactPerson(e.target.value)}
            placeholder={isHomeowner ? "e.g. John Smith" : "e.g. Maria Lopez (intake)"}
            maxLength={120}

          />
        </div>
      </div>

      <div className="space-y-1.5">
        <Label className="text-xs">{isHomeowner ? "Call Notes" : "Summary"}</Label>
        <Textarea
          value={summary}
          onChange={(e) => setSummary(e.target.value)}
          placeholder={isHomeowner ? "What was discussed on the call?" : "What was discussed or sent?"}
          maxLength={500}
          className="min-h-[64px] text-sm"

        />
        <p className="text-[10px] text-muted-foreground text-right">{summary.length}/500</p>
      </div>

      <div className="space-y-1.5">
        <Label className="text-xs">Next Step (optional)</Label>
        <Input
          value={nextStep}
          onChange={(e) => setNextStep(e.target.value)}
          placeholder="e.g. Resubmit revised plans by Friday"
          maxLength={500}

        />
      </div>

      <div className="space-y-1.5">
        <Label className="text-xs">Follow-up Date (optional)</Label>
        <Input
          type="datetime-local"
          value={followUpLocal}
          onChange={(e) => setFollowUpLocal(e.target.value)}

        />
      </div>

      <div className="flex items-center justify-between pt-1">
        <span className="text-[11px] text-muted-foreground">
          Logged by <span className="text-foreground font-medium">{loggedBy}</span>
        </span>
        <div className="flex gap-2">
          {onCancel && (
            <Button variant="ghost" size="sm" onClick={onCancel}>Cancel</Button>
          )}
          <Button size="sm" onClick={submit}>
            {supersedesId ? "Append Correction" : (isHomeowner ? "Log Call" : "Append Interaction")}
          </Button>
        </div>
      </div>
    </div>
  );
}
